import {
  INPUT_NO_INPUT,
  INPUT_NO_MATCH,
  INPUT_PROMPT,
  SHARE_COPY_IMPOSSIBLE,
  SHARE_COPY_SUCCESS
} from './consts';
import { GameService } from './game';
import { $, $id, onClick, onEnterPress, onEscPress, onInput } from './helpers';
import { RegionService } from './regions';
import { StorageService } from './storage';
import { GameGuess, GameState, Region, UIElements } from './types';


export class UIService {
  private els: UIElements = {} as UIElements;
  private gameService?: GameService;

  private regionNames: string[] = [];

  constructor(
    private regionService: RegionService,
    private storageService: StorageService
  ) {
    // Inputs
    let regionInput = $id('region-input') as HTMLInputElement;
    let submitBtn = $id('submit-guess') as HTMLButtonElement;
    let statsBtn = $id('stats-btn') as HTMLButtonElement;
    let shareBtn = $id('share-btn') as HTMLButtonElement;
    let closeModalBtn = $id('close-modal') as HTMLButtonElement;

    // Outputs / Displays
    let attemptsCount = $id('attempts-count');
    let attemptsText = $id('attempts-text');
    let guessesList = $id('guesses-list');
    let autocompleteList = $id('autocomplete-list');
    let regionSvgContainer = $('.region-svg-container');
    let gameEndButtons = $id('game-end-buttons');
    let statsModal = $id('stats-modal');
    let gamesPlayedStat = $id('games-played');
    let winPercentageStat = $id('win-percentage');
    let currentStreakStat = $id('current-streak');
    let maxStreakStat = $id('max-streak');

    this.els = {
      regionInput: regionInput,
      submitBtn: submitBtn,
      attemptsCount: attemptsCount,
      attemptsText: attemptsText,
      guessesList: guessesList,
      autocompleteList: autocompleteList,
      regionSvgContainer: regionSvgContainer,
      
      // Game end buttons
      gameEndButtons: gameEndButtons,
      statsBtn: statsBtn,
      shareBtn: shareBtn,
      
      // Modal elements
      statsModal: statsModal,
      closeModalBtn: closeModalBtn,
      
      // Stats elements
      gamesPlayedStat: gamesPlayedStat,
      winPercentageStat: winPercentageStat,
      currentStreakStat: currentStreakStat,
      maxStreakStat: maxStreakStat,
    };

    // Input changes
    onInput(this.els.regionInput, this.onInput.bind(this));

    // Form submission
    onClick(this.els.submitBtn, this.onGuess.bind(this));
    onEnterPress(this.els.regionInput, this.onGuess.bind(this));

    // Header button events
    onClick(this.els.statsBtn, this.showStatsModal.bind(this));
    onClick(this.els.shareBtn, this.shareResults.bind(this));

    // Close modal events
    onClick(this.els.closeModalBtn, (() => this.setShowModal(false)).bind(this));
    onClick(this.els.statsModal, ((e: MouseEvent) => {
      if (e.target === this.els.statsModal) {
        this.setShowModal(false);
      }
    }).bind(this));
    onEscPress((() => this.setShowModal(false)).bind(this));
  }

  
  public onInit(gameService: GameService): void {
    this.updateAttemptsDisplay();
    this.gameService = gameService;
    this.regionNames = this.regionService.getAllRegionNames();

    this.els.autocompleteList!.style.display = 'none';
  }

  private onInput(): void {
    if (!this.els.regionInput || !this.els.submitBtn) return;

    this.clearAutocomplete();
    
    const input = this.els.regionInput.value.trim();
    
    if (input.length === 0) {
      this.els.submitBtn.disabled = false;
      this.clearAutocomplete();
      return;
    }

    this.populateAutocomplete(input);

    // If is valid region, enable submit button
    const region = this.regionService?.findRegionByName(input);
    this.els.submitBtn.disabled = !region;
  }
  
  private onGuess(): void {
    if (!this.els.regionInput)
      return;
    
    const input = this.els.regionInput.value.trim();
    
    if (!input) {
      this.showInputError(INPUT_NO_INPUT);
      return;
    }
    
    const region = this.regionService?.findRegionByName(input);
    if (!region) {
      this.showInputError(INPUT_NO_MATCH);
      return;
    }
    
    // Clear any previous errors
    this.clearInputError();
    
    if (typeof this.gameService !== 'undefined') {
      this.gameService.makeGuess(region);
    }
    
    // Clear input
    this.els.regionInput.value = '';
  }

  // -------- AUTOCOMPLETE METHODS --------- //
  private populateAutocomplete(input: string): void {
    if (!this.els.regionInput || !this.els.autocompleteList) return;

    if (!input) {
      this.clearAutocomplete();
      return;
    }

    const matches = this.regionNames.filter(name => {
      const input_ascii = input.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const name_ascii = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      return name_ascii.includes(input_ascii);
    });

    if (matches.length === 0) {
      this.clearAutocomplete();
      return;
    }

    matches.forEach(name => {
      const item = document.createElement('div');
      item.className = 'autocomplete-item';

      const input_ascii = input.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const name_ascii = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const idx = name_ascii.indexOf(input_ascii);
      item.innerHTML = name.slice(0, idx) + 
               '<strong>' + name.slice(idx, idx + input.length) + '</strong>' + 
               name.slice(idx + input.length);
      item.onclick = () => {
          this.els.regionInput!.value = name;
          this.els.submitBtn!.disabled = false;
          this.clearAutocomplete();
      };
      this.els.autocompleteList!.appendChild(item);
    });
    this.els.autocompleteList.style.display = 'block';
  }

  private clearAutocomplete(): void {
    this.els.autocompleteList!.innerHTML = '';
    this.els.autocompleteList!.style.display = 'none';
  }
  // ------- /AUTOCOMPLETE METHODS --------- //
  
  private showInputError(message: string): void {
    if (!this.els.regionInput) return;
    
    this.els.regionInput.classList.add('error');
    this.els.regionInput.placeholder = message;
    this.els.regionInput.value = '';
    
    setTimeout(this.clearInputError.bind(this), 3000);
  }
  
  private clearInputError(): void {
    this.els.regionInput?.classList.remove('error');
    this.els.regionInput!.placeholder = INPUT_PROMPT;
  }
  
  public updateAttemptsDisplay(remaining: number = 5): void {
    if (!this.els.attemptsCount) return;
    
    this.els.attemptsCount.textContent = remaining.toString();
    
    // Change color based on remaining attempts
    if (remaining <= 1) {
      this.els.attemptsCount.style.color = 'var(--danger-color)';
    } else if (remaining <= 2) {
      this.els.attemptsCount.style.color = 'var(--warning-color)';
    } else {
      this.els.attemptsCount.style.color = 'var(--primary-color)';
    }
  }
  
  public addGuessToDisplay(guess: GameGuess, isCorrect: boolean): void {
    if (!this.els.guessesList) return;
    
    const guessElement = document.createElement('div');
    guessElement.className = `guess-item ${isCorrect ? 'correct' : 'incorrect'} fade-in`;
    
    if (isCorrect) {
      // For correct guesses, keep the header + content structure
      const guessName = document.createElement('div');
      guessName.className = 'guess-name';
      guessName.textContent = guess.region.name;
      
      const guessFeedback = document.createElement('div');
      guessFeedback.className = 'guess-feedback';
      guessFeedback.innerHTML = this.formatFeedback(guess, isCorrect);
      
      guessElement.appendChild(guessName);
      guessElement.appendChild(guessFeedback);
    } else {
      // For incorrect guesses, no header
      const guessFeedback = document.createElement('div');
      guessFeedback.className = 'guess-feedback';
      guessFeedback.innerHTML = this.formatFeedback(guess, isCorrect);
      
      guessElement.appendChild(guessFeedback);
    }
    
    this.els.guessesList.appendChild(guessElement);
    
    // Scroll to the new guess
    guessElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
  
  private formatFeedback(guess: GameGuess, isCorrect: boolean): string {
    const feedback = guess.feedback;
    if (isCorrect) {
      return `
        <strong>✅ Correct!</strong><br>
        <strong>Population</strong>: ${guess.region.population?.toLocaleString()} habitants<br>
        <strong>Villes principales</strong>: ${guess.region.majorCities?.map(city => city.name).join(', ')}<br>
        <strong>Caractéristiques</strong>: ${guess.region.features?.join(', ')}<br>
        <strong>Industries</strong>: ${guess.region.industries?.join(', ')}
    `;
    } else {
      const arrow = this.regionService?.getDirectionArrow(feedback.direction) || '❓';
      return `
        <div><strong>${feedback.guessedRegion}</strong></div>
        <div><strong>${feedback.distance} km</strong></div>
        <div>${arrow}</div>
      `;
    }
  }
  
  public loadRegionSVG(svgFileName: string): void {
    if (!this.els.regionSvgContainer) return;
    
    const svgPath = `data/boundaries/${svgFileName}`;
    
    // Show loading state
    this.els.regionSvgContainer.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 0.9rem;">
        <div style="text-align: center;">
          <div style="font-size: 1.5rem; margin-bottom: 10px;">⏳</div>
          <div>Chargement...</div>
        </div>
      </div>
    `;
    
    // Load SVG file
    fetch(svgPath)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load SVG: ${response.status}`);
        }
        return response.text();
      })
      .then(svgContent => {
        if (!this.els.regionSvgContainer) return;
        
        this.els.regionSvgContainer.innerHTML = svgContent;
        
        // Style the SVG for better display
        const svg = this.els.regionSvgContainer.querySelector('svg');
        if (svg) {
          svg.style.maxWidth = '100%';
          svg.style.maxHeight = '100%';
          svg.style.width = 'auto';
          svg.style.height = 'auto';
          
          // Change fill color to primary color
          const paths = svg.querySelectorAll('path, polygon, rect, circle, ellipse');
          paths.forEach(path => {
            (path as HTMLElement).style.fill = 'var(--primary-color)';
          });
        }
        
        console.log(`✅ SVG loaded: ${svgFileName}`);
      })
      .catch(error => {
        console.error('❌ Error loading SVG:', error);
        this.els.regionSvgContainer!.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #666; font-size: 0.9rem;">
            <div style="text-align: center;">
              <div style="font-size: 2rem; margin-bottom: 10px;">🗺️</div>
              <div>Contour de la région à deviner</div>
              <div style="font-size: 0.8rem; margin-top: 5px; opacity: 0.7; color: var(--danger-color);">
                Erreur de chargement: ${svgFileName}
              </div>
            </div>
          </div>
        `;
      });
  }

  public onGameEnd(won: boolean, correctRegion: Region, attempts: number): void {
    if (!won) {
      this.displayCorrectAnswer(correctRegion)
    }
    this.updateStatsDisplay();
    this.showGameEndButtons();
    console.log(won ? `🎉 Game won in ${attempts} attempts!` : `😞 Game lost. Answer was ${correctRegion.name}`);
  }

  private displayCorrectAnswer(correctRegion: Region): void {
    if (!this.els.guessesList) return;
    const element = document.createElement('div')

    element.className = 'guess-item correction fade-in';
    
    const guessName = document.createElement('div');
    guessName.className = 'guess-name';
    guessName.textContent = `Bonne réponse: ${correctRegion.name}`;
    
    const guessFeedback = document.createElement('div');
    guessFeedback.className = 'guess-feedback';
    guessFeedback.innerHTML = `
      <strong>🎯 La bonne réponse était:</strong><br>
      <strong>Population</strong>: ${correctRegion.population?.toLocaleString()} habitants<br>
      <strong>Villes principales</strong>: ${correctRegion.majorCities?.map(city => city.name).join(', ')}<br>
      <strong>Caractéristiques</strong>: ${correctRegion.features?.join(', ')}<br>
      <strong>Industries</strong>: ${correctRegion.industries?.join(', ')}
    `;
    
    element.appendChild(guessName);
    element.appendChild(guessFeedback);

    this.els.guessesList.appendChild(element);
  }


  // --------- STATS MODAL METHODS --------- //
  public showStatsModal(): void {
    this.updateStatsDisplay();
    this.setShowModal(true);
  }
  
  private updateStatsDisplay(): void {
    const stats = this.storageService?.getStats();
    if (!stats) return;
    
    if (this.els.gamesPlayedStat) {
      this.els.gamesPlayedStat.textContent = stats.gamesPlayed.toString();
    }
    if (this.els.winPercentageStat) {
      const winPercentage = this.storageService?.getWinPercentage() || 0;
      this.els.winPercentageStat.textContent = winPercentage + '%';
    }
    if (this.els.currentStreakStat) {
      this.els.currentStreakStat.textContent = stats.currentStreak.toString();
    }
    if (this.els.maxStreakStat) {
      this.els.maxStreakStat.textContent = stats.maxStreak.toString();
    }
  }

  private setShowModal(visible: boolean): void {
    if (visible) {
      this.els.statsModal?.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    } else {
      this.els.statsModal?.classList.add('hidden');
      document.body.style.overflow = '';
    }
  }
  // -------- /STATS MODAL METHODS --------- //
  

  // --------- COPY RESULTS METHODS --------- //
  private shareResults(): void {
    if (typeof this.gameService === 'undefined') return;
    
    const gameState = this.gameService.getGameState();
    if (!gameState.gameComplete) return;
    
    let resultText = this.createShareText(gameState);
    let resultTextWithLink = resultText + '\n' + window.location.href;
    
    if (navigator.share) {
      const shareData = {
        title: 'Québecle',
        text: resultText,
        url: window.location.href
      };
      navigator.share(shareData).catch(error => {
        console.log('Error sharing:', error);
        this.copyToClipboard(resultTextWithLink);
      });
    } else {
      this.copyToClipboard(resultTextWithLink);
    }
  }
  
  private createShareText(gameState: GameState): string {
    const date = new Date().toLocaleDateString('fr-CA');
    const result = gameState.won ? `${gameState.attempts}/5` : 'X/5';
    
    let shareText = `⚜️ Québecle ⚜️\n ${date} : ${result}\n\n`;
    
    gameState.guesses.forEach((guess) => {
      shareText += this.visualizeCloseness(guess.feedback.closeness) + '\n';
    });
    
    if (!gameState.won) {
      const remaining = 5 - gameState.attempts;
      shareText += '⏹️⏹️⏹️⏹️⏹️\n'.repeat(remaining);
    }
    
    return shareText;
  }

  private visualizeCloseness(closeness: number): string {
    const BLUE = '🟦';
    const YELLOW = '🟨';
    const WHITE = '⬜';

    const scaled = closeness * 5;
    const full = Math.floor(scaled);
    const frac = scaled - full;

    const hasHalf = full < 5 && frac >= 0.5 ? true : false;
    const totalFilled = Math.min(5, full + (hasHalf ? 1 : 0));

    return BLUE.repeat(full) + (hasHalf ? YELLOW : '') + WHITE.repeat(5 - totalFilled);
  }
  
  private copyToClipboard(text: string): void {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        this.showToast(SHARE_COPY_SUCCESS);
      }).catch(error => {
        console.error('Error copying to clipboard:', error);
        this.fallbackCopyToClipboard(text);
      });
    } else {
      this.fallbackCopyToClipboard(text);
    }
  }
  
  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      this.showToast(SHARE_COPY_SUCCESS);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      this.showToast(SHARE_COPY_IMPOSSIBLE);
    }
    
    document.body.removeChild(textArea);
  }
  
  private showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    toast.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: var(--dark-color);
        color: var(--secondary-color);
        padding: 12px 20px;
        border-radius: var(--border-radius);
        z-index: 2000;
        animation: fadeIn 0.3s ease-in;
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.animation = 'fadeOut 0.3s ease-out';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 3000);
  }
  // -------- /COPY RESULTS METHODS --------- //
  
  // Disable game input (when game is complete)
  public disableInput(): void {
    if (this.els.regionInput) {
      this.els.regionInput.disabled = true;
      this.els.regionInput.placeholder = 'Partie terminée';
    }
    if (this.els.submitBtn) {
      this.els.submitBtn.disabled = true;
    }
  }

  public enableInput(): void {
    if (this.els.regionInput) {
      this.els.regionInput.disabled = false;
      this.els.regionInput.placeholder = 'Entrez le nom d\'une région...';
    }
    if (this.els.submitBtn) {
      this.els.submitBtn.disabled = false;
    }
  }

  public clearGuesses(): void {
    if (this.els.guessesList) {
      this.els.guessesList.innerHTML = '';
    }
  }

  public showGameEndButtons(): void {
    this.els.gameEndButtons?.classList.remove('hidden');
    // Scroll to bottom of page to show buttons
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  }
  
  public hideGameEndButtons(): void {
    this.els.gameEndButtons?.classList.add('hidden');
  }
}

